import * as React from 'react';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';
import { Box, Button, Flex, Icon, Text } from '@opendoor/bricks/core';
import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';

import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { submitAndGoToNextPage } from 'src/store/ui';

import InfoCard from '../../common/InfoCard';

interface INavigationControlProps {
  pageError?: string;
  previousPath?: string;
  hideBackButton?: boolean;
  hideNextButton?: boolean;
  infoCard?: string;
  isStickyOnMobile?: boolean;
  showDoThisLater?: boolean;
  canAdvance?: boolean;
  nextButtonText?: string;
  onDoThisLater?: () => void;
  onNext?: () => void;
  currentSlug?: string;
}

const NavContainer = styled.div<{ isStickyOnMobile: boolean }>`
  box-sizing: border-box;
  width: 100%;
  z-index: 99;
  display: flex;
  ${(props) =>
    props.isStickyOnMobile &&
    `
    ${media.smallerThan.XS} {
      width: 100%;
    }`}
`;

const NavigationControl: React.FC<INavigationControlProps> = ({
  pageError,
  previousPath,
  hideBackButton,
  hideNextButton,
  infoCard,
  isStickyOnMobile = false,
  showDoThisLater = false,
  canAdvance = true,
  nextButtonText = 'Next',
  onDoThisLater,
  onNext,
  currentSlug,
}) => {
  const noButtonsToDisplay =
    (!previousPath || hideBackButton) && hideNextButton && !showDoThisLater;
  const appDispatch = useAppDispatch();

  const handleSubmit = () => {
    onNext?.();
    appDispatch(submitAndGoToNextPage());
  };

  const loading = useAppSelector((state) => state.ui.pageLoading);
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);
  const largerButtonWidth = atEnd || nextButtonText === 'View Offer';
  const handleDoThisLater = () => {
    onDoThisLater && onDoThisLater();
  };

  return (
    <NavContainer isStickyOnMobile={isStickyOnMobile ?? true}>
      {pageError && (
        <Box mt="4">
          <Text fontSize="s0" color="terracotta50" textAlign="center">
            {pageError}
          </Text>
        </Box>
      )}
      {infoCard && (
        <Flex mt="4" mb={[0, 7]} mx={5} justifyContent="center">
          <Box>
            <InfoCard text={infoCard} />
          </Box>
        </Flex>
      )}
      {showDoThisLater && !hideNextButton && (
        <Button
          display={['block', 'none']}
          mt="4"
          variant="link"
          onClick={handleDoThisLater}
          analyticsName={`reception-fe-${currentSlug}-skip`}
          aria-label={null}
        >
          Do this later
        </Button>
      )}
      <Flex
        mt={noButtonsToDisplay ? 0 : [showDoThisLater && !hideNextButton ? 3 : 5, 0]}
        mx={'auto'}
        width="100%"
        justifyContent={
          (hideNextButton && !hideBackButton) || (!previousPath && !loading)
            ? 'flex-end'
            : 'space-between'
        }
        alignItems="center"
      >
        {!hideBackButton && previousPath && (
          <Link
            style={{
              textDecoration: 'none',
            }}
            to={previousPath}
            tabIndex={-1}
          >
            <Button
              variant="icon"
              analyticsName={`reception-exclusives-back-${currentSlug}`}
              aria-label="go back"
              trackImpression
              onClick={() => {}}
            >
              <Icon name="chevron-left" color="neutrals100" size={16} />
            </Button>
          </Link>
        )}
        <Flex flexDirection="column" alignItems="center">
          {showDoThisLater ? (
            <Button
              display={[hideNextButton ? 'block' : 'none', 'block']}
              mt={hideNextButton ? 0 : 4}
              variant="secondary"
              onClick={handleDoThisLater}
              width="275px"
              analyticsName={`reception-fe-${currentSlug}-skip`}
              aria-label={null}
            >
              Do this later
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={!canAdvance || loading}
              width={largerButtonWidth ? '140px' : '92px'}
              minHeight="44px"
              analyticsName={`exclusive-reception-fe-${currentSlug}-${nextButtonText}`}
              trackImpression={true}
              rounded="semiRounded"
              aria-label={null}
              type="submit"
              id="od-form-submit"
              px="5"
              py="0"
            >
              <Flex justifyContent="space-between" width="100%" alignItems="center">
                {nextButtonText}
                {/* @ts-expect-error - icon size isn't standard */}
                <Icon name="arrow-right" size={20} color="neutrals0" />
              </Flex>
            </Button>
          )}
        </Flex>
      </Flex>
    </NavContainer>
  );
};

export default NavigationControl;
